import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { Upload, message } from "antd";
import {
  ArrowLeft,
  Camera,
  Trash2,
  Upload as UploadIcon,
  Download,
  FileSpreadsheet,
  FileText,
  FileJson,
  CheckCircle,
  CheckSquare,
  Square,
  Info,
  X,
  RefreshCw,
  Import as TransferIn,
} from "lucide-react";
import axios from "axios";
import API_BASE from "../config/api";
import BottomTabBar from "../components/BottomTabBar";

const MAX_EXTRACTION_UPLOAD_BYTES = 1024 * 1024; // 1MB target after compression

/**
 * Compresses an image file on the client-side to keep it under 1MB
 * @param {File} file - Original uploaded file
 * @param {number} maxWidth - Maximum width boundary (1600px is perfect for OpenAI)
 * @param {number} quality - Quality level from 0.1 to 1.0 (default 0.7)
 * @returns {Promise<File>} Compressed File object
 */
const compressImageForExtraction = (file, maxWidth = 1600, quality = 0.7) => {
  return new Promise((resolve, reject) => {
    if (file.size < 800 * 1024) {
      return resolve(file);
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return reject(new Error("Canvas compression failed"));
            }
            const sanitizedName = file.name
              .toLowerCase()
              .replace(/[^a-z0-9.]/g, "-")
              .replace(/-+/g, "-");

            const compressedFile = new File([blob], sanitizedName, {
              type: "image/jpeg",
              lastModified: Date.now(),
            });
            resolve(compressedFile);
          },
          "image/jpeg",
          quality,
        );
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

const DataExtractionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const webcamRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [imageSource, setImageSource] = useState("");
  const [uploadFileList, setUploadFileList] = useState([]);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [captureMode, setCaptureMode] = useState("scanner");
  const [extractedSections, setExtractedSections] = useState([]);

  const [activeTab, setActiveTab] = useState("scan");
  const [importFormat, setImportFormat] = useState("excel");
  const [exportFormat, setExportFormat] = useState("excel");
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedExportIds, setSelectedExportIds] = useState([]);

  const createFileFromBase64 = async (base64DataUrl) => {
    const response = await fetch(base64DataUrl);
    const blob = await response.blob();
    return new File([blob], "captured-menu.jpg", { type: "image/jpeg" });
  };

  const clearSelectedImage = () => {
    if (previewUrl && imageSource === "uploader") {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl("");
    setImageSource("");
    setUploadFileList([]);
    setExtractedSections([]);
  };

  const handleCapture = async () => {
    if (!isCameraReady) {
      message.warning("Camera is not ready yet. Please allow access and try again.");
      return;
    }

    try {
      const screenshot = webcamRef.current?.getScreenshot();
      if (!screenshot) {
        message.error("Unable to capture image. Check camera permissions and try again.");
        return;
      }
      const file = await createFileFromBase64(screenshot);
      setSelectedFile(file);
      setPreviewUrl(screenshot);
      setImageSource("scanner");
      setCaptureMode("scanner");
      setUploadFileList([]);
      message.success("Photo captured.");
    } catch (error) {
      console.error("Capture error:", error);
      message.error("Failed to capture photo.");
    }
  };

  const handleCameraReady = () => {
    setIsCameraReady(true);
    setCameraError("");
  };

  const handleCameraError = (error) => {
    setIsCameraReady(false);
    console.error("Camera access error:", error);
    setCameraError(error?.message || "Camera access failed.");
    message.error("Cannot access camera. Allow permission or use Upload Image.");
  };

  const handleUploadChange = ({ file, fileList }) => {
    const raw = file?.originFileObj || file;
    if (!raw || !(raw instanceof File)) return;
    if (!raw.type?.startsWith("image/")) {
      message.error("Please select a valid image file.");
      return;
    }

    if (previewUrl && imageSource === "uploader") {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(raw);
    setPreviewUrl(URL.createObjectURL(raw));
    setImageSource("uploader");
    setCaptureMode("uploader");
    setUploadFileList(fileList.slice(-1));
  };

  useEffect(() => {
    return () => {
      if (previewUrl && imageSource === "uploader") {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, imageSource]);

  const handleProcess = async () => {
    if (!selectedFile) {
      message.warning("Capture or upload an image first.");
      return;
    }

    setIsProcessing(true);
    try {
      console.log(
        `Original image size: ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`,
      );
      const processedFile = await compressImageForExtraction(selectedFile);
      console.log(
        `Ready to upload: ${(processedFile.size / 1024).toFixed(2)} KB`,
      );

      if (processedFile.size > MAX_EXTRACTION_UPLOAD_BYTES) {
        message.error(
          "Image is still too large after compression. Try a smaller photo or lower resolution.",
        );
        return;
      }

      const formData = new FormData();
      formData.append("image", processedFile);
      const response = await axios.post(`${API_BASE}/extract`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const sections = response?.data?.sections;
      if (!Array.isArray(sections)) {
        throw new Error("Invalid extraction response.");
      }
console.log(sections , "sections");
      setExtractedSections(sections);
      message.success("Data extracted successfully. Click Add to merge into menu.");
    } catch (error) {
      console.error("Process error:", error);
      message.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to process image for extraction.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddExtractedData = () => {
    if (!extractedSections.length) {
      message.warning("Process an image first.");
      return;
    }

    navigate("/ai-menu", {
      state: {
        sections: extractedSections,
        baseSections: location.state?.currentSections || [],
      },
    });
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExcel = [
      "application/vnd.openxmlformats-" +
        "officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];
    const allowedCsv = ["text/csv", "text/plain"];
    const allowedJson = ["application/json"];

    const isValid =
      (importFormat === "excel" &&
        allowedExcel.some(
          (t) =>
            file.type === t ||
            file.name.endsWith(".xlsx") ||
            file.name.endsWith(".xls"),
        )) ||
      (importFormat === "csv" &&
        (allowedCsv.includes(file.type) || file.name.endsWith(".csv"))) ||
      (importFormat === "json" &&
        (allowedJson.includes(file.type) || file.name.endsWith(".json")));

    if (!isValid) {
      message.error(
        `Please select a valid ${importFormat.toUpperCase()} file.`,
      );
      return;
    }
    setImportFile(file);
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      message.warning("Select a file first.");
      return;
    }
    setImporting(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const content = e.target.result;
        let sections = [];

        if (importFormat === "json") {
          try {
            const parsed = JSON.parse(content);
            sections = Array.isArray(parsed)
              ? parsed
              : parsed.sections || [];
          } catch {
            message.error("Invalid JSON format.");
            setImporting(false);
            return;
          }
        } else if (importFormat === "csv") {
          const lines = content.split("\n").filter(Boolean);
          const headers = lines[0]
            .split(",")
            .map((h) => h.trim().toLowerCase());
          const nameIdx = headers.indexOf("name");
          const priceIdx = headers.indexOf("price");
          const catIdx = headers.indexOf("category");
          const descIdx = headers.indexOf("description");

          const itemMap = {};
          lines.slice(1).forEach((line) => {
            const cols = line.split(",");
            const name = cols[nameIdx]?.trim() || "";
            const price = cols[priceIdx]?.trim() || "0";
            const category = cols[catIdx]?.trim() || "General";
            const description =
              descIdx >= 0 ? cols[descIdx]?.trim() || "" : "";

            if (!name) return;
            if (!itemMap[category]) {
              itemMap[category] = [];
            }
            itemMap[category].push({
              id: Date.now().toString() + Math.random(),
              name,
              price,
              description,
              isVisible: true,
            });
          });

          sections = Object.entries(itemMap).map(([name, items]) => ({
            id: Date.now().toString() + Math.random(),
            name,
            items,
          }));
        } else {
          message.info(
            "Excel import requires the SheetJS library. Falling back to CSV format.",
          );
          setImporting(false);
          return;
        }

        if (!sections.length) {
          message.warning("No data found in file.");
          setImporting(false);
          return;
        }

        navigate("/ai-menu", {
          state: {
            sections,
            baseSections: location.state?.currentSections || [],
          },
        });
      };

      if (importFormat === "json" || importFormat === "csv") {
        reader.readAsText(importFile);
      }
    } catch (err) {
      console.error(err);
      message.error("Import failed.");
      setImporting(false);
    }
  };

  const menuSections =
    location.state?.currentSections || location.state?.sections || [];

  const getSectionKey = (section, index) => section.id || `section-${index}`;

  useEffect(() => {
    setSelectedExportIds(menuSections.map(getSectionKey));
  }, [location.state]);

  const toggleExportSection = (sectionKey) => {
    setSelectedExportIds((prev) =>
      prev.includes(sectionKey)
        ? prev.filter((id) => id !== sectionKey)
        : [...prev, sectionKey],
    );
  };

  const selectAllExportSections = () => {
    setSelectedExportIds(menuSections.map(getSectionKey));
  };

  const clearExportSectionSelection = () => {
    setSelectedExportIds([]);
  };

  const selectedExportSections = menuSections.filter((section, index) =>
    selectedExportIds.includes(getSectionKey(section, index)),
  );

  const handleExport = () => {
    if (!menuSections.length) {
      message.warning("No menu data to export. Add sections first.");
      return;
    }

    if (!selectedExportSections.length) {
      message.warning("Select at least one section to export.");
      return;
    }

    setExporting(true);

    try {
      let content = "";
      let filename = "";
      let mimeType = "";

      if (exportFormat === "json") {
        content = JSON.stringify(selectedExportSections, null, 2);
        filename = `menu-${Date.now()}.json`;
        mimeType = "application/json";
      } else if (exportFormat === "csv") {
        const rows = ["Name,Price,Category,Description,Visible"];
        selectedExportSections.forEach((section) => {
          (section.items || []).forEach((item) => {
            rows.push(
              [
                `"${item.name || ""}"`,
                `"${item.price || ""}"`,
                `"${section.name || ""}"`,
                `"${(item.description || "").replace(/"/g, '""')}"`,
                item.isVisible !== false ? "true" : "false",
              ].join(","),
            );
          });
        });
        content = rows.join("\n");
        filename = `menu-${Date.now()}.csv`;
        mimeType = "text/csv";
      } else {
        message.info(
          "Excel export uses CSV format. Open in Excel to convert.",
        );
        const rows = ["Name,Price,Category,Description"];
        selectedExportSections.forEach((section) => {
          (section.items || []).forEach((item) => {
            rows.push(
              [
                `"${item.name || ""}"`,
                `"${item.price || ""}"`,
                `"${section.name || ""}"`,
                `"${item.description || ""}"`,
              ].join(","),
            );
          });
        });
        content = rows.join("\n");
        filename = `menu-${Date.now()}.csv`;
        mimeType = "text/csv";
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success("Menu exported successfully!");
    } catch (err) {
      console.error(err);
      message.error("Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const extractedItemCount = extractedSections.reduce(
    (sum, s) => sum + (s.items?.length || 0),
    0,
  );
  const selectedExportItemCount = selectedExportSections.reduce(
    (sum, s) => sum + (s.items?.length || 0),
    0,
  );
  const selectedVisibleItems = selectedExportSections.reduce(
    (sum, s) =>
      sum + (s.items || []).filter((i) => i.isVisible !== false).length,
    0,
  );

  const inputCls = `w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500`;

  const FormatSelector = ({ value, onChange, label }) => (
    <div className="mb-3">
      <label className="text-xs sm:text-sm text-gray-600 font-medium mb-2 block">
        {label}
      </label>
      <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
        {[
          {
            id: "excel",
            label: "Excel",
            sub: ".xlsx",
            icon: <FileSpreadsheet size={16} />,
            bg: "bg-green-50",
            color: "text-green-700",
            selBg: "bg-green-50",
            selBorder: "border-green-500",
          },
          {
            id: "csv",
            label: "CSV",
            sub: ".csv",
            icon: <FileText size={16} />,
            bg: "bg-orange-50",
            color: "text-orange-700",
            selBg: "bg-orange-50",
            selBorder: "border-orange-500",
          },
          {
            id: "json",
            label: "JSON",
            sub: ".json",
            icon: <FileJson size={16} />,
            bg: "bg-blue-50",
            color: "text-blue-700",
            selBg: "bg-blue-50",
            selBorder: "border-blue-500",
          },
        ].map((fmt) => (
          <div
            key={fmt.id}
            role="button"
            tabIndex={0}
            onClick={() => onChange(fmt.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onChange(fmt.id);
            }}
            className={`flex flex-col items-center gap-1 sm:gap-1.5 p-2 sm:p-2.5 rounded-xl border-2 cursor-pointer transition-colors touch-manipulation min-h-[72px] sm:min-h-0 ${
              value === fmt.id
                ? `${fmt.selBg} ${fmt.selBorder}`
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center ${fmt.bg} ${fmt.color}`}
            >
              {fmt.icon}
            </div>
            <span
              className={`text-xs font-semibold ${
                value === fmt.id ? fmt.color : "text-gray-700"
              }`}
            >
              {fmt.label}
            </span>
            <span className="text-[10px] text-gray-400">{fmt.sub}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="h-[100dvh] overflow-hidden bg-gray-100 flex flex-col w-full min-w-0 overflow-x-hidden">
      {/* HEADER */}
      <div className="bg-white px-3 sm:px-4 pb-3 sm:pb-4 border-b border-gray-100 shadow-sm flex items-center justify-between gap-2 flex-shrink-0"
        style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}
      >
        <button
          type="button"
          onClick={() => navigate("/ai-menu")}
          className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-600 border border-gray-200 px-2.5 sm:px-3 py-2 min-h-[40px] rounded-full flex-shrink-0 touch-manipulation active:bg-gray-50"
        >
          <ArrowLeft size={13} /> Back
        </button>
        <div className="text-center min-w-0 flex-1 px-2">
          <h1 className="text-base sm:text-lg font-extrabold text-gray-900 truncate">
            Extract Data
          </h1>
          <p className="text-[10px] sm:text-xs text-gray-500">
            Scan, import or export menu data
          </p>
        </div>
        <div className="w-16 sm:w-20 flex-shrink-0" />
      </div>

      {/* TAB SWITCHER */}
      <div className="px-3 sm:px-4 pt-3 flex-shrink-0 max-w-lg sm:max-w-xl md:max-w-2xl w-full mx-auto">
        <div className="flex bg-gray-100 rounded-2xl p-1 gap-1">
          {[
            { id: "scan", label: "Scan", icon: <Camera size={13} /> },
            { id: "import", label: "Import", icon: <UploadIcon size={13} /> },
            { id: "export", label: "Export", icon: <Download size={13} /> },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 py-2.5 sm:py-2 min-h-[44px] rounded-xl text-[11px] sm:text-xs font-semibold transition-all touch-manipulation ${
                activeTab === tab.id
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-400"
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
        <div
          className="px-3 sm:px-4 pt-3 pb-24 sm:pb-28 max-w-lg sm:max-w-xl md:max-w-2xl w-full mx-auto flex flex-col gap-3 min-w-0"
          style={{
            paddingBottom: "max(6rem, calc(5.5rem + env(safe-area-inset-bottom)))",
          }}
        >
        {/* SCAN TAB */}
        {activeTab === "scan" && (
          <>
            <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <p className="text-xs font-semibold text-gray-600 mb-2">
                Choose input method
              </p>
              <div className="flex gap-2">
                {["scanner", "uploader"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => setCaptureMode(mode)}
                    className={`flex-1 py-2.5 sm:py-2 min-h-[44px] rounded-full text-xs font-semibold transition-colors touch-manipulation ${
                      captureMode === mode
                        ? "bg-blue-600 text-white"
                        : "border border-gray-200 text-gray-600 bg-white"
                    }`}
                  >
                    {mode === "scanner" ? "Scanner" : "Upload image"}
                  </button>
                ))}
              </div>
            </div>

            {captureMode === "scanner" && (
              <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-bold text-gray-900 mb-2">Scanner</p>
                <div className="rounded-xl overflow-hidden bg-gray-900 w-full aspect-[9/16] max-h-64 sm:max-h-80 mb-2">
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    mirrored={false}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={1}
                    forceScreenshotSourceSize
                    onUserMedia={handleCameraReady}
                    onUserMediaError={handleCameraError}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    videoConstraints={{
                      facingMode: { ideal: "environment" },
                      width: { ideal: 720 },
                      height: { ideal: 1280 },
                    }}
                  />
                </div>
                <p
                  className={`text-xs mb-3 ${
                    cameraError
                      ? "text-red-500"
                      : isCameraReady
                        ? "text-green-500"
                        : "text-gray-400"
                  }`}
                >
                  {cameraError
                    ? `Camera issue: ${cameraError}`
                    : isCameraReady
                      ? "✓ Camera ready"
                      : "Waiting for camera…"}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={handleCapture}
                    disabled={!isCameraReady}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 text-white text-xs font-bold py-3 sm:py-2.5 min-h-[44px] rounded-full disabled:opacity-40 touch-manipulation active:bg-blue-700"
                  >
                    <Camera size={13} /> Scan image
                  </button>
                  {imageSource === "scanner" && previewUrl && (
                    <button
                      type="button"
                      onClick={clearSelectedImage}
                      className="flex items-center justify-center gap-1 bg-red-50 text-red-500 text-xs font-semibold px-3 py-3 sm:py-2.5 min-h-[44px] rounded-full touch-manipulation shrink-0"
                    >
                      <Trash2 size={13} /> Delete
                    </button>
                  )}
                </div>
                {imageSource === "scanner" && previewUrl && (
                  <div className="mt-3 rounded-xl overflow-hidden border border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 px-3 pt-2.5 mb-1.5">
                      Captured photo
                    </p>
                    <img
                      src={previewUrl}
                      alt="Captured menu"
                      className="w-full max-h-36 object-contain rounded-xl"
                    />
                  </div>
                )}
              </div>
            )}

            {captureMode === "uploader" && (
              <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-bold text-gray-900 mb-3">
                  Upload image
                </p>
                <div className="w-full [&_.ant-upload]:!w-full [&_.ant-upload]:!block">
                <Upload
                  beforeUpload={() => false}
                  onChange={handleUploadChange}
                  maxCount={1}
                  accept="image/*"
                  showUploadList={false}
                  className="w-full block"
                >
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-5 sm:p-6 w-full flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:border-blue-400 touch-manipulation">
                    <UploadIcon
                      size={28}
                      className="text-blue-500 mb-2"
                    />
                    <p className="text-xs font-semibold text-blue-600">
                      Upload sample menu
                    </p>
                    <p className="text-[10px] text-gray-400 mt-1">
                      JPG, PNG up to 10MB
                    </p>
                  </div>
                </Upload>
                </div>
                {imageSource === "uploader" && previewUrl && (
                  <button
                    type="button"
                    onClick={clearSelectedImage}
                    className="mt-3 flex items-center gap-1.5 bg-red-50 text-red-500 text-xs font-semibold px-3 py-2 rounded-full"
                  >
                    <Trash2 size={13} /> Delete uploaded
                  </button>
                )}
              </div>
            )}

            {previewUrl && (
              <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
                <p className="text-sm font-bold text-gray-900 mb-2">Preview</p>
                <img
                  src={previewUrl}
                  alt="Menu preview"
                  className="w-full max-h-48 object-contain rounded-xl"
                />
              </div>
            )}

            <button
              type="button"
              onClick={handleProcess}
              disabled={!selectedFile || isProcessing}
              className="w-full bg-blue-600 text-white font-bold text-sm py-3.5 min-h-[48px] rounded-full flex items-center justify-center gap-2 disabled:opacity-40 touch-manipulation active:bg-blue-700"
            >
              {isProcessing ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Processing…
                </>
              ) : (
                <>
                  <Camera size={14} /> Process & extract data
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleAddExtractedData}
              disabled={!extractedSections.length}
              className="w-full border-2 border-blue-600 text-blue-600 font-bold text-sm py-3.5 min-h-[48px] rounded-full flex items-center justify-center gap-2 disabled:opacity-40 disabled:border-gray-200 disabled:text-gray-400 touch-manipulation active:bg-blue-50"
            >
              <TransferIn size={14} /> Add to menu
            </button>

            {extractedSections.length > 0 && (
              <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText
                      size={16}
                      className="text-blue-600 flex-shrink-0"
                    />
                    <p className="text-sm font-bold text-gray-900 truncate">
                      Extracted Data Preview
                    </p>
                  </div>
                  <span className="text-[10px] sm:text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full flex-shrink-0">
                    {extractedItemCount}{" "}
                    {extractedItemCount === 1 ? "item" : "items"}
                  </span>
                </div>

                <div className="space-y-4">
                  {extractedSections.map((section, sectionIdx) => (
                    <div key={section.id || section.name || sectionIdx}>
                      <p className="text-xs font-bold text-blue-600 mb-2 tracking-wide border-b pb-1 uppercase">
                        {section.name || "Untitled"}
                      </p>
                      <div className="space-y-2.5">
                        {(section.items || []).map((item, itemIdx) => (
                          <div
                            key={item.id || `${sectionIdx}-${itemIdx}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-gray-800 font-medium text-xs sm:text-sm">
                                  {item.name}
                                </p>
                                {item.description ? (
                                  <p className="text-[11px] text-gray-400 mt-0.5">
                                    {item.description}
                                  </p>
                                ) : null}
                              </div>
                              {item.price != null && item.price !== "" ? (
                                <span className="font-mono text-gray-900 font-bold text-xs sm:text-sm flex-shrink-0">
                                  {item.price}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* IMPORT TAB */}
        {activeTab === "import" && (
          <>
            <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <FormatSelector
                value={importFormat}
                onChange={(f) => {
                  setImportFormat(f);
                  setImportFile(null);
                }}
                label="Select file format"
              />

              <label
                htmlFor="import-file-input"
                className="border-2 border-dashed border-gray-300 rounded-2xl p-5 sm:p-6 flex flex-col items-center justify-center cursor-pointer bg-gray-50 hover:border-blue-400 transition-colors block w-full touch-manipulation"
              >
                <UploadIcon size={28} className="text-blue-500 mb-2" />
                <p className="text-xs font-semibold text-blue-600 mb-1">
                  Upload{" "}
                  {importFormat === "excel"
                    ? "Excel file"
                    : importFormat === "csv"
                      ? "CSV file"
                      : "JSON file"}
                </p>
                <p className="text-[10px] text-gray-400">
                  {importFormat === "excel"
                    ? ".xlsx, .xls up to 10MB"
                    : importFormat === "csv"
                      ? ".csv up to 10MB"
                      : ".json up to 10MB"}
                </p>
                <input
                  id="import-file-input"
                  type="file"
                  className="hidden"
                  accept={
                    importFormat === "excel"
                      ? ".xlsx,.xls"
                      : importFormat === "csv"
                        ? ".csv"
                        : ".json"
                  }
                  onChange={handleImportFile}
                />
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-3 flex gap-2.5 items-start">
              <Info size={15} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-blue-800 mb-0.5">
                  Expected format
                </p>
                {importFormat === "excel" || importFormat === "csv" ? (
                  <p className="text-[10px] text-blue-600 leading-relaxed">
                    Columns: <strong>Name</strong>, <strong> Price</strong>,{" "}
                    <strong> Category</strong>, Description (optional). Each
                    row = one menu item.
                  </p>
                ) : (
                  <p className="text-[10px] text-blue-600 leading-relaxed">
                    JSON array of sections:{" "}
                    <strong>
                      [{"{"}name, items[{"{"}name, price, description{"}"}]{"}"}
                      ]
                    </strong>
                  </p>
                )}
              </div>
            </div>

            {importFile && (
              <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
                <p className="text-xs font-bold text-gray-900 mb-2">
                  File ready
                </p>
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100">
                  <div
                    className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      importFormat === "excel"
                        ? "bg-green-50"
                        : importFormat === "csv"
                          ? "bg-orange-50"
                          : "bg-blue-50"
                    }`}
                  >
                    {importFormat === "excel" ? (
                      <FileSpreadsheet
                        size={18}
                        className="text-green-700"
                      />
                    ) : importFormat === "csv" ? (
                      <FileText size={18} className="text-orange-700" />
                    ) : (
                      <FileJson size={18} className="text-blue-700" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-900 truncate">
                      {importFile.name}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setImportFile(null)}
                    className="p-1 flex-shrink-0"
                  >
                    <X size={15} className="text-gray-400" />
                  </button>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={handleImportSubmit}
              disabled={!importFile || importing}
              className="w-full bg-blue-600 text-white font-bold text-sm py-3.5 min-h-[48px] rounded-full flex items-center justify-center gap-2 disabled:opacity-40 touch-manipulation active:bg-blue-700"
            >
              {importing ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Importing…
                </>
              ) : (
                <>
                  <UploadIcon size={14} /> Import to menu
                </>
              )}
            </button>
          </>
        )}

        {/* EXPORT TAB */}
        {activeTab === "export" && (
          <>
            <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <FormatSelector
                value={exportFormat}
                onChange={setExportFormat}
                label="Choose export format"
              />
            </div>

            <div className="bg-white rounded-2xl p-3 sm:p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between gap-2 mb-3">
                <p className="text-sm font-bold text-gray-900">
                  Select sections to export
                </p>
                {menuSections.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      type="button"
                      onClick={selectAllExportSections}
                      disabled={
                        selectedExportIds.length === menuSections.length
                      }
                      className="text-[10px] sm:text-xs font-semibold text-blue-600 px-2 py-1 rounded-full bg-blue-50 disabled:opacity-40 touch-manipulation"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={clearExportSectionSelection}
                      disabled={!selectedExportIds.length}
                      className="text-[10px] sm:text-xs font-semibold text-gray-500 px-2 py-1 rounded-full bg-gray-100 disabled:opacity-40 touch-manipulation"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {menuSections.length === 0 ? (
                <div className="py-6 flex flex-col items-center text-gray-400">
                  <Info size={24} className="mb-2 opacity-30" />
                  <p className="text-xs text-center">
                    No menu data found. Add sections first from the AI Menu
                    screen.
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-1.5 sm:gap-2 mb-3">
                    {[
                      {
                        label: "Selected",
                        value: selectedExportSections.length,
                        color: "text-blue-600",
                      },
                      {
                        label: "Items",
                        value: selectedExportItemCount,
                        color: "text-blue-600",
                      },
                      {
                        label: "Visible",
                        value: selectedVisibleItems,
                        color: "text-green-500",
                      },
                    ].map((stat) => (
                      <div
                        key={stat.label}
                        className="bg-gray-50 rounded-xl p-2 text-center border border-gray-100"
                      >
                        <p
                          className={`text-lg font-extrabold ${stat.color}`}
                        >
                          {stat.value}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          {stat.label}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    {menuSections.map((section, index) => {
                      const sectionKey = getSectionKey(section, index);
                      const isSelected = selectedExportIds.includes(sectionKey);

                      return (
                        <button
                          key={sectionKey}
                          type="button"
                          onClick={() => toggleExportSection(sectionKey)}
                          className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 border text-left transition-colors touch-manipulation ${
                            isSelected
                              ? "bg-blue-50 border-blue-200"
                              : "bg-gray-50 border-gray-100 opacity-75"
                          }`}
                        >
                          {isSelected ? (
                            <CheckSquare
                              size={18}
                              className="text-blue-600 flex-shrink-0"
                            />
                          ) : (
                            <Square
                              size={18}
                              className="text-gray-300 flex-shrink-0"
                            />
                          )}
                          <div className="w-8 h-8 rounded-xl bg-white flex items-center justify-center flex-shrink-0 border border-gray-100">
                            <FileText size={14} className="text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-gray-900 truncate">
                              {section.name}
                            </p>
                            <p className="text-[10px] text-gray-400">
                              {section.items?.length || 0} items
                            </p>
                          </div>
                          {isSelected && (
                            <CheckCircle
                              size={14}
                              className="text-green-500 flex-shrink-0"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              onClick={handleExport}
              disabled={!selectedExportSections.length || exporting}
              className="w-full bg-blue-600 text-white font-bold text-sm py-3.5 min-h-[48px] rounded-full flex items-center justify-center gap-2 disabled:opacity-40 touch-manipulation active:bg-blue-700"
            >
              {exporting ? (
                <>
                  <RefreshCw size={14} className="animate-spin" />
                  Exporting…
                </>
              ) : (
                <>
                  <Download size={14} />
                  Export as{" "}
                  {exportFormat === "excel"
                    ? "Excel"
                    : exportFormat === "csv"
                      ? "CSV"
                      : "JSON"}
                </>
              )}
            </button>
          </>
        )}
        </div>
      </div>

      <BottomTabBar />
    </div>
  );
};

export default DataExtractionPage;
